import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { get } from 'lodash';
import {
  distributionMapSelector,
  intervalSelector,
  currentMetricsMetaSelector,
} from '@store/common/selectors';
import { chartOption } from '../utils/chartSchema';
import { CHART_TYPE } from './utils';
import useDistribution from './useDistribution';
import useZoom from './useZoom';
import useAnnotations from '@components/annotations/hooks/useAnnotations';
import useTooltipSync from './useTooltipSync';
import { INVERTED_METRIC_SWITCH_MAP, METRIC_SWITCH_MAP } from '@utils/metrics';
import useSelect from './useSelect';
import { TOOLS } from '../control-panel/Toolbar';
import useMetricFilterSeries from './useMetricFilterSeries';
import useReset from './useReset';
import useAnnotateBrush from '@components/annotations/hooks/useAnnotateBrush';
import useManualAlerts from './alerts/useManualAlerts';
import useAIAlertSeries from './alerts/useAIAlertSeries';
import useLegend from './useLegend';
import useAIAlerts from './alerts/useAIAlerts';
import useDodSeries from './useDodSeries';
import useCursor from './useCursor';
import useMetricSeries from './useMetricSeries';
import { unitAtom } from '../atoms';
import useCompareSeries from './useCompareSeries';
import useToggleSeries from './useToggleSeries';

function useToolbox({ metric, activeTool, appliedFilter, naMode, diagMode, isSelected = false }) {
  const distributionMetrics = useSelector(distributionMapSelector);
  const [chartMode, setChartMode] = useState(CHART_TYPE.LINE);
  const [instance, setInstance] = useState(null);
  const unit = useAtomValue(unitAtom);
  const currentMetricsMeta = useSelector(currentMetricsMetaSelector);

  const chartMetric = unit === '%' ? get(INVERTED_METRIC_SWITCH_MAP, metric, metric) : metric;
  const metricChartOption = chartOption(metric, currentMetricsMeta);

  // chart instance with init option
  const chartInstanceRefCb: any = useCallback((instance) => {
    setInstance(instance);
    instance.setOption(metricChartOption);
  }, []);

  // main metric timeseries
  useMetricSeries({
    metric: chartMetric, // for unit toggle
    instance,
    enabled: [CHART_TYPE.LINE, CHART_TYPE.BAR].includes(chartMode),
    additionalTriggerDeps: [chartMode],
  });

  // distribution series
  const { distributionQueryState, distribution, selectDistribution } = useDistribution({
    instance,
    metric,
    enabled: metric in distributionMetrics && chartMode === CHART_TYPE.DISTRIBUTION,
    additionalTriggerDeps: [],
  });

  // enable/disable cursor control for burshing action on chart
  useCursor({
    instance,
    activeTool,
    additionalTriggerDeps: [chartMode],
  });

  // double click to undo zoom/select
  useReset({ instance, enabled: true });

  // handle zoom interaction
  useZoom({
    instance,
    chartMode,
    enabled: [TOOLS.zoom, TOOLS.zoomToSelect].includes(activeTool),
  });

  // tooltip sync between charts
  useTooltipSync({
    instance,
    tag: metric,
    enabled: !naMode && (!diagMode || isSelected),
  });

  // handle select interaction
  useSelect({
    instance,
    chartMode,
    enabled: [TOOLS.select, TOOLS.zoomToSelect].includes(activeTool),
    metric,
  });

  // handle drawing of annotation
  useAnnotateBrush({
    instance,
    appliedFilter,
    metricKey: metric,
    enabled: activeTool === TOOLS.annotate,
  });

  // render annotation markers
  useAnnotations({
    metric: INVERTED_METRIC_SWITCH_MAP[metric] || metric,
    instance,
    enabled: [CHART_TYPE.LINE, CHART_TYPE.BAR].includes(chartMode),
    intervalSelector,
  });

  // metric filter series
  useMetricFilterSeries({
    metric: chartMetric,
    instance,
    additionalTriggerDeps: [chartMode],
  });

  // manual alert event markers
  useManualAlerts({
    metric: METRIC_SWITCH_MAP[metric] || metric,
    instance,
    enabled: [CHART_TYPE.LINE, CHART_TYPE.BAR].includes(chartMode),
    additionalTriggerDeps: [chartMode],
    drilldown: activeTool !== TOOLS.annotate,
  });

  // ai alert time series, pcp and devices
  const { legendSeries: aiAlertLegendSeries } = useAIAlertSeries({
    // metric based on current active unit toggle state
    // if metric is null then the alert will be disabled
    metric: chartMetric,
    instance,
    enabled: [CHART_TYPE.LINE, CHART_TYPE.BAR].includes(chartMode),
    additionalTriggerDeps: [chartMode],
  });

  // ai alert event markers
  useAIAlerts({
    metric: METRIC_SWITCH_MAP[metric] || metric,
    instance,
    enabled: [CHART_TYPE.LINE, CHART_TYPE.BAR].includes(chartMode),
    additionalTriggerDeps: [chartMode],
    drilldown: activeTool !== TOOLS.annotate,
  });

  // day over day timeseries
  useDodSeries({
    metric: chartMetric, // for unit toggle
    instance,
    enabled: [CHART_TYPE.LINE].includes(chartMode),
    additionalTriggerDeps: [chartMode],
  });

  useCompareSeries({
    metric: chartMetric, // for unit toggle
    instance,
    enabled: [CHART_TYPE.LINE].includes(chartMode),
    additionalTriggerDeps: [chartMode],
  });

  // legend, currently used only for ai alerts
  const { toggleLegend } = useLegend({ instance, enabled: true, seriesList: aiAlertLegendSeries });

  // highlight series, used with compare
  useToggleSeries({ instance, enabled: true });

  // debug
  // window.chartInsace = instance;

  return {
    chartMode,
    setChartMode,
    chartInstanceRefCb,
    distributionQueryState,
    instance,
    toggleLegend,
    distribution,
    selectDistribution,
  };
}

export default useToolbox;
